import { useState } from "react";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LineSketch } from "@/components/quote/LineSketch";
import { allFixed, COMMON, dirsOf, flipDir, NO_MOVING, togglePanel } from "@/lib/sliding";

// Sliding panels picker (Phase 25): count, common-configuration tiles (drawn like the bi-fold fold picker), tap panels X/O,
// a direction per moving panel and equal or typed widths. inline = Designer grid; otherwise the chosen tile + "Change".
const tileSize = (n) => ({ w: n * 24, h: 80 });
const art = (kind, cfg, dirs, extra = {}) => ({ sketch: kind, mapped: true, ...tileSize(cfg.length), config: cfg, dirs: dirsOf(cfg, dirs), dims: false, bold: true, ...extra });

function Tile({ kind, cfg, on, disabled, onPick, testId }) {
  return (
    <button type="button" role="radio" aria-checked={on} disabled={disabled} data-testid={testId} onClick={onPick}
      className={`flex min-h-[44px] flex-col items-center gap-0.5 rounded-xl border-2 px-1 pb-1 pt-1.5 transition-colors ${on ? "border-[#0071E3] bg-[#E8F1FD]" : "border-black/10 bg-white hover:border-[#0071E3]/50"} ${disabled ? "cursor-default opacity-40" : ""}`}>
      <LineSketch params={art(kind, cfg, "")} boxW={96} boxH={72} className="h-[40px] w-[64px] bg-transparent" />
      <span className={`font-sans tracking-wider text-[13px] font-bold ${on ? "text-[#0071E3]" : "text-black"}`}>{cfg}</span>
    </button>
  );
}

function Panels({ cfg, dirs, widths, readOnly, onChange, testId }) {
  const d = dirsOf(cfg, dirs), ws = widths.length === cfg.length ? widths : [];
  return (
    <div className="flex gap-1" data-testid={`${testId}-panels`}>
      {[...cfg].map((c, i) => (
        <div key={i} className="flex min-w-0 flex-col gap-1" style={{ flex: ws.length ? ws[i] : 1 }}>
          <button type="button" disabled={readOnly} data-testid={`${testId}-panel-${i}`} aria-pressed={c === "X"} onClick={() => onChange(togglePanel(cfg, dirs, i))}
            title={c === "X" ? "Moving: tap to make it fixed" : "Fixed: tap to make it move"}
            className={`h-12 rounded-lg border-2 font-sans text-xl font-bold transition-colors ${c === "X" ? "border-[#0071E3] bg-[#E8F1FD] text-[#0071E3]" : "border-black/15 bg-white text-[#1D1D1F]"}`}>{c}</button>
          {c === "X" ? (
            <button type="button" disabled={readOnly} data-testid={`${testId}-dir-${i}`} onClick={() => onChange(flipDir(cfg, dirs, i))} title="Slide direction: tap to flip"
              className="grid h-9 place-items-center rounded-lg border border-black/10 bg-white text-[#0071E3]">{d[i] === "L" ? <ArrowLeft className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}</button>
          ) : <span className="h-9" />}
        </div>
      ))}
    </div>
  );
}

function Widths({ n, w, widths, readOnly, onChange, testId }) {
  const typed = widths.length === n;
  const sum = widths.reduce((s, x) => s + Number(x || 0), 0);
  return (
    <div className="space-y-1.5" data-testid={`${testId}-widths`}>
      <label className="flex items-center gap-2 text-[13px] text-[#1D1D1F]">
        <input type="checkbox" disabled={readOnly} data-testid={`${testId}-equal`} checked={!typed}
          onChange={(e) => onChange({ panel_widths: e.target.checked ? [] : Array(n).fill(+(w / n).toFixed(3)) })} /> Equal panel widths
      </label>
      {typed && (
        <div className="flex gap-1">
          {widths.map((x, i) => (
            <input key={i} data-testid={`${testId}-width-${i}`} inputMode="decimal" disabled={readOnly} defaultValue={x} aria-label={`Panel ${i + 1} width`}
              onBlur={(e) => { const v = Number(e.target.value); if (v > 0) onChange({ panel_widths: widths.map((y, k) => (k === i ? v : y)) }); }}
              className="h-9 min-w-0 flex-1 rounded-md border border-black/10 bg-white px-1.5 text-center text-[13px] text-black" />
          ))}
        </div>
      )}
      {typed && Math.abs(sum - w) > 0.13 && <p data-testid={`${testId}-width-note`} className="text-xs font-semibold text-[#C93400]">Panel widths add up to {+sum.toFixed(2)} in, the unit is {w} in</p>}
    </div>
  );
}

function Editor({ kind, cfg, dirs, widths, w, readOnly, onChange, testId, cols, onPicked }) {
  const n = cfg.length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5" role="radiogroup" aria-label="Panels">
        <span className="mr-1 text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">Panels</span>
        {[2, 3, 4, 5, 6].map((k) => (
          <button key={k} type="button" role="radio" aria-checked={k === n} disabled={readOnly} data-testid={`${testId}-count-${k}`}
            onClick={() => k !== n && onChange({ config: COMMON[k][0], slide_dirs: "", panel_widths: [] })}
            className={`h-10 w-10 rounded-full border text-[14px] font-semibold ${k === n ? "border-[#0071E3] bg-[#0071E3] text-white" : "border-black/10 bg-white text-black"}`}>{k}</button>
        ))}
      </div>
      <div className={`grid gap-2 ${cols}`} role="radiogroup" aria-label="Configuration">
        {(COMMON[n] || []).map((c) => (
          <Tile key={c} kind={kind} cfg={c} on={c === cfg} disabled={readOnly && c !== cfg} testId={`${testId}-tile-${c}`}
            onPick={() => { if (!readOnly) { onChange({ config: c, slide_dirs: "" }); onPicked?.(); } }} />
        ))}
      </div>
      <p className="text-[12px] text-[#6D6D72]">Custom: tap a panel to switch Moving (X) / Fixed (O); tap an arrow to flip its slide direction.</p>
      <Panels cfg={cfg} dirs={dirs} widths={widths} readOnly={readOnly} onChange={onChange} testId={testId} />
      {w > 0 && <Widths n={n} w={w} widths={widths} readOnly={readOnly} onChange={onChange} testId={testId} />}
      {allFixed(cfg) && <p data-testid={`${testId}-error`} className="text-sm font-semibold text-[#D70015]">{NO_MOVING}</p>}
    </div>
  );
}

export function SlidePicker({ kind = "sliding", config, dirs = "", widths = [], w = 0, onChange, readOnly, testId = "slide-picker", inline = false, note = "" }) {
  const cfg = /^[XO]{2,6}$/.test(config || "") ? config : "XO";
  const [open, setOpen] = useState(false);
  const props = { kind, cfg, dirs, widths: (widths || []).map(Number), w: Number(w) || 0, readOnly, onChange, testId };
  if (inline) {
    return (
      <div data-testid={testId} className="col-span-full w-full basis-full">
        <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">Sliding panels · <span className="font-sans font-bold tracking-wider text-[#0071E3]" data-testid={`${testId}-value`}>{cfg}</span>{readOnly ? " (locked)" : ""}</p>
        <Editor {...props} cols="grid-cols-[repeat(auto-fill,minmax(92px,1fr))]" />
      </div>
    );
  }
  return (
    <div data-testid={testId} className="w-full basis-full">
      <p className="mb-1 text-xs font-semibold text-slate-400">Panels{readOnly ? " (locked)" : ""}</p>
      <Popover open={open && !readOnly} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={readOnly} data-testid={`${testId}-open`}
            className={`flex min-h-[44px] items-center gap-2 rounded-xl border-2 bg-[#E8F1FD] py-1 pl-1 pr-3 disabled:cursor-default ${allFixed(cfg) ? "border-[#D70015]" : "border-[#0071E3]"}`}>
            <LineSketch params={art(kind, cfg, dirs, widths?.length === cfg.length ? { widths: widths.join(",") } : {})} boxW={96} boxH={72} className="h-[48px] w-[64px] bg-transparent" />
            <span className="font-sans tracking-wider text-[14px] font-bold text-[#0071E3]" data-testid={`${testId}-value`}>{cfg}</span>
            {!readOnly && <span className="flex items-center text-xs font-semibold text-[#0071E3]">Change <ChevronDown className="h-3.5 w-3.5" /></span>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" collisionPadding={12} className="max-h-[var(--radix-popover-content-available-height)] w-[min(92vw,480px)] overflow-y-auto p-3" data-testid={`${testId}-sheet`}>
          <p className="mb-2 text-sm font-semibold text-[#1D1D1F]">Sliding panels · {cfg.length} panels</p>
          <Editor {...props} cols="grid-cols-4 sm:grid-cols-5" />
        </PopoverContent>
      </Popover>
      {allFixed(cfg) && <p data-testid={`${testId}-error-inline`} className="mt-1 text-[11px] font-semibold text-red-400">{NO_MOVING}</p>}
      {note && <p data-testid={`${testId}-track-note`} className="mt-1 text-[11px] font-semibold text-amber-400">{note}</p>}
    </div>
  );
}
